/**
 * SiteMapContextMenu — the streamed-context toggle menu on the map's
 * top-left action rail (below Import), promoted out of the bottom-left
 * SiteMapLayersPanel (2026-08-18, William): context streaming is a MAP
 * DISPLAY control like the basemap, not site evidence, so it lives with
 * the display controls and the bottom panel stays purely "Site layers".
 *
 * Rows are plain <label>s (not DropdownMenuItem) so a toggle click keeps
 * the menu open — enabling several datasets is the common gesture.
 * Testids keep the historical `layers-context-*` names so host wiring
 * stays 1:1 traceable across the move.
 */
import React from 'react';
import { ChevronDown, Radar } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export interface ContextMenuDataset {
  id: string;
  label: string;
  color: string;
  enabled: boolean;
  status?: {
    loading?: boolean;
    unavailable?: boolean;
    count?: number;
    truncated?: boolean;
  };
}

export const SiteMapContextMenu: React.FC<{
  datasets: ReadonlyArray<ContextMenuDataset>;
  onToggle: (id: string) => void;
}> = ({ datasets, onToggle }) => {
  const enabledCount = datasets.filter(d => d.enabled).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="da-btn map-action"
        data-testid="site-map-menu-context"
      >
        <Radar aria-hidden />
        <span className="flex-1 text-left">Context</span>
        <span className="type-meta text-text-muted" data-testid="layers-context-enabled-count">
          {enabledCount > 0 ? `${enabledCount} streaming` : 'off'}
        </span>
        <ChevronDown aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[15rem]"
        data-testid="layers-context-section"
      >
        <div className="px-2 pb-1 pt-1.5 text-[9px] text-text-muted/80">
          Streamed for this viewport — not saved to the site.
        </div>
        <div className="px-1 pb-1">
          {datasets.map(d => {
            const statusText = d.status?.unavailable
              ? 'n/a'
              : d.status?.loading
                ? '…'
                : d.status
                  ? `${d.status.count}${d.status.truncated ? '+' : ''}`
                  : null;
            return (
              <label
                key={d.id}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 hover:bg-state-hover"
                data-testid={`layers-context-row-${d.id}`}
              >
                <input
                  type="checkbox"
                  checked={d.enabled}
                  onChange={() => onToggle(d.id)}
                  className="h-3 w-3 shrink-0 accent-primary"
                  data-testid={`layers-context-toggle-${d.id}`}
                  aria-label={`${d.enabled ? 'Stop' : 'Start'} streaming ${d.label}`}
                />
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: d.color }}
                  aria-hidden
                />
                <span
                  className={`flex-1 truncate text-[10px] ${d.enabled ? 'text-text-primary' : 'text-text-muted'}`}
                  title={d.status?.truncated
                    ? `${d.label} — viewport capped; zoom in for full coverage`
                    : d.label}
                >
                  {d.label}
                </span>
                {d.enabled && statusText != null && (
                  <span
                    className="shrink-0 rounded border border-border-subtle px-1 text-[9px] tabular-nums tracking-wider text-text-muted"
                    data-testid={`layers-context-status-${d.id}`}
                  >
                    {statusText}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
